// ====================================
// File: templates/MinimalistResumeTemplate.jsx
// ====================================
import React from "react";

const MinimalistResumeTemplate = ({ data }) => {
  if (!data) return null;
  const { header, mainContent, sidebarContent } = data;

  // Helper function to clean bullet points
  const renderBulletPoints = (text) => {
    if (!text) return null;

    return text
      .split("\n")
      .map((line, idx) => {
        const cleanLine = line.replace(/^[•·▪▸-]\s*/, "").trim();
        if (!cleanLine) return null;

        return (
          <div key={idx} className="minimalist-bullet-point">
            {cleanLine}
          </div>
        );
      })
      .filter(Boolean);
  };

  return (
    <div
      className="minimalist-resume"
      style={{ width: "100%", boxSizing: "border-box" }}
    >
      {/* Header Section */}
      <div className="minimalist-header">
        <h1 className="minimalist-name">{header.name || "YOUR NAME"}</h1>
        <div className="minimalist-title">
          {header.subtitle || "Professional Title"}
        </div>
        <div className="minimalist-contact">
          {header.contactInfo?.map((contact, idx) => (
            <React.Fragment key={idx}>
              <span className="contact-item">{contact.value}</span>
              {idx < header.contactInfo.length - 1 && (
                <span className="contact-separator"> • </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div
        className="minimalist-body"
        style={{ maxWidth: "100%", width: "100%", margin: "0" }}
      >
        {/* Main Content First */}
        {mainContent?.map((section) => (
          <div key={section.id} className="minimalist-section">
            <h2 className="minimalist-section-title">{section.title}</h2>
            <div className="minimalist-section-content">
              {section.type === "profile" && (
                <div className="minimalist-profile">{section.content}</div>
              )}

              {section.type === "experience" && (
                <div className="minimalist-experiences">
                  {section.items?.map((item, index) => (
                    <div key={item.id} className="minimalist-job">
                      <div className="minimalist-job-header">
                        <h3 className="minimalist-job-title">
                          {item.jobTitle}
                        </h3>
                        <span className="minimalist-job-date">
                          {item.companyInfo?.split("|")[2]?.trim() || ""}
                        </span>
                      </div>
                      <div className="minimalist-company-info">
                        {item.companyInfo?.split("|").slice(0, 2).join(" | ")}
                      </div>
                      <div className="minimalist-details">
                        {renderBulletPoints(item.details)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.type === "volunteer" && (
                <div className="minimalist-experiences">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-job">
                      <div className="minimalist-job-header">
                        <h3 className="minimalist-job-title">{item.role}</h3>
                        <span className="minimalist-job-date">
                          {item.organization?.split("|")[1]?.trim() || ""}
                        </span>
                      </div>
                      <div className="minimalist-company-info">
                        {item.organization?.split("|")[0]?.trim() ||
                          item.organization}
                      </div>
                      <div className="minimalist-details">
                        {renderBulletPoints(item.details)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.type === "projects" && (
                <div className="minimalist-experiences">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-job">
                      <div className="minimalist-job-header">
                        <h3 className="minimalist-job-title">{item.title}</h3>
                        <span className="minimalist-job-date">
                          {item.period || ""}
                        </span>
                      </div>
                      <div className="minimalist-company-info">
                        {item.technologies}
                      </div>
                      <div className="minimalist-details">
                        {renderBulletPoints(item.description)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.type === "achievements" && (
                <ul className="minimalist-achievements">
                  {section.items?.map((item) => (
                    <li key={item.id} className="minimalist-achievement-item">
                      <strong>{item.title}</strong>
                      {item.description && `: ${item.description}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}

        {/* Sidebar Content */}
        {sidebarContent?.map((section) => (
          <div key={section.id} className="minimalist-section">
            <h2 className="minimalist-section-title">{section.title}</h2>
            <div className="minimalist-section-content">
              {section.type === "education" && (
                <div className="minimalist-education">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-edu-item">
                      <div className="minimalist-edu-header">
                        <strong className="minimalist-degree">
                          {item.degree}
                        </strong>
                        <span className="minimalist-edu-date">
                          {item.period}
                        </span>
                      </div>
                      <div className="minimalist-institution">
                        {item.institution}
                      </div>
                      {item.details && (
                        <div className="minimalist-edu-details">
                          {renderBulletPoints(item.details)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {["competencies", "technical", "interests"].includes(
                section.type
              ) && (
                <div className="minimalist-skills">
                  {section.items?.map((item, idx) => (
                    <span key={idx} className="minimalist-skill">
                      {typeof item === "string" ? item : item.name}
                    </span>
                  ))}
                </div>
              )}

              {section.type === "languages" && (
                <div className="minimalist-languages">
                  {section.items?.map((item, idx) => (
                    <div key={idx} className="minimalist-language-item">
                      <span className="minimalist-language-name">
                        {item.name}
                      </span>
                      {item.proficiency && (
                        <span className="minimalist-language-proficiency">
                          ({item.proficiency})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.type === "certifications" && (
                <div className="minimalist-certifications">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-cert-item">
                      <span className="minimalist-cert-name">{item.name}</span>
                      {item.period && (
                        <span className="minimalist-cert-date">
                          {" "}
                          - {item.period}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.type === "awards" && (
                <div className="minimalist-awards">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-award-item">
                      <div className="minimalist-award-header">
                        <strong className="minimalist-award-name">
                          {item.name}
                        </strong>
                        {item.year && (
                          <span className="minimalist-award-year">
                            {item.year}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <div className="minimalist-award-desc">
                          {item.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.type === "publications" && (
                <div className="minimalist-publications">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-pub-item">
                      <strong className="minimalist-pub-title">
                        {item.title}
                      </strong>
                      <div className="minimalist-pub-info">
                        {item.publisher}
                        {item.year && `, ${item.year}`}
                      </div>
                      {item.description && (
                        <div className="minimalist-pub-desc">
                          {item.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.type === "social" && (
                <div className="minimalist-social">
                  {section.items?.map((item) => (
                    <div key={item.id} className="minimalist-social-item">
                      <strong className="minimalist-social-title">
                        {item.title}
                      </strong>
                      {item.organization && (
                        <div className="minimalist-social-org">
                          {item.organization}
                        </div>
                      )}
                      {item.description && (
                        <div className="minimalist-social-desc">
                          {renderBulletPoints(item.description)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MinimalistResumeTemplate;
